import { Fragment, useContext, useEffect, useState } from "react"
import { createEvent, editEvent, deleteEvent } from "../../actions/calendar"
import { CalendarContext } from "../../contexts/CalendarProvider"
import { useAuth } from "../../hooks/useAuth"
import { handleSubmitPromise } from "../../utils/handleSubmitPromise"
import { DefaultFooter, Input, LoadingBox, MaskedInput, SmartForm } from "../extras/SmartForm"
import { ConfirmForm } from "../extras/ConfirmForm"
import { CloseButton } from "../static/Buttons"
import { ErrorMessage, SuccessMessage } from "../static/Messages"
import { FlexContainer } from "../containers/FlexContainer"
import { getLongDate } from "../../utils/calendar"
import { ROLES, hasRole } from "../../config/roles"

// ── CreateEvent (sync modal — from Header button) ────────────────────

export const CreateEvent = {
    Header: () => (
        <Fragment><i className="fas fa-1x fa-calendar-plus"></i> Añadir nuevo evento</Fragment>
    ),
    Body: ({ onClose, modalState }) => {
        const [, dispatch] = useContext(CalendarContext)
        const [user] = useAuth()

        const handleSubmit = (data, notify) => new Promise((resolve, reject) => {
            dispatch(createEvent(user.token, data, notify), resolve, reject)
        })

        return(
            <div className="uk-modal-body om-body">
                <EventForm onSubmit={handleSubmit} onClose={onClose} modalState={modalState} isCreate={true}>
                    <button className="uk-button uk-button-primary" type="submit"><i className="fa fa-plus"></i> <span className="hide-s">Añadir</span></button>
                </EventForm>
            </div>
        )
    }
}

// ── CalendarEventDetail (async modal — detail with edit/remove subviews) ─

export const CalendarEventDetail = ({ elem, onClose }) => {
    const [view, setView] = useState('detail')
    const [, dispatch] = useContext(CalendarContext)
    const [user] = useAuth()

    useEffect(() => {
        setView('detail')
    }, [elem])

    if(view === 'edit') {
        const handleSubmit = (data, notify) => new Promise((resolve, reject) => {
            dispatch(editEvent(user.token, elem.uuid, data, notify), resolve, reject)
        })
        return(
            <div className="uk-modal-body om-body">
                <EventForm event={elem} onSubmit={handleSubmit} onClose={onClose} modalState={true}>
                    <button className="uk-button uk-button-primary" type="submit"><i className="fa fa-save"></i> <span className="hide-s">Guardar</span></button>
                </EventForm>
            </div>
        )
    }

    if(view === 'remove') {
        const handleSubmit = () => new Promise((resolve, reject) => {
            dispatch(deleteEvent(user.token, elem.uuid), resolve, reject)
        })
        return(
            <div className="uk-modal-body">
                <div className="om-body">
                    <ConfirmForm onClose={onClose} onSubmit={handleSubmit} modalState={true} buttonStyle="danger" buttonText="Eliminar" buttonIcon="fa-trash">
                        <p className="uk-text-justify uk-margin-remove-top">¿De verdad desea eliminar el evento <b>{elem.type}: {elem.description}</b>? Esta acción no puede ser revertida.</p>
                    </ConfirmForm>
                </div>
            </div>
        )
    }

    return(
        <div className="uk-modal-body om-body uk-text-left">
            <CloseButton onClick={onClose}/>
            <p className="uk-margin-remove-top">
                <span><i className="fas fa-tag"></i> <b>Tipo asignado</b></span>
                <span className="type"> {elem.type}</span>
                <span style={{marginLeft: '3.5%'}}>
                    <span style={{color: '#333'}}><i className="fas fa-calendar-day"></i> <b>Fecha</b></span>
                    <span className="date"> {getLongDate(elem.when)}</span>
                </span>
            </p>
            <p>
                <span><i className="fas fa-clipboard-list"></i> <b>Descripción o Motivo</b></span>
                <span className="description"> {elem.description}</span>
            </p>
            <p>
                <span><i className="fas fa-user-clock"></i> <b>Hora de Citación</b></span>
                <span className="time"> {new Date(elem.when).toLocaleTimeString('es', {"minute": "2-digit","hour": "2-digit"})} CEST</span>
                <span style={{marginLeft: '3.5%'}}>
                    <span style={{color: '#333'}}><i className="fas fa-business-time"></i> <b>Hora del Evento</b></span>
                    <span className="plus"> {new Date(parseInt(elem.when)+parseInt(elem.plus)).toLocaleTimeString('es', {"minute": "2-digit","hour": "2-digit"})} CEST</span>
                </span>
            </p>
            <p>
                <span><i className="fas fa-map-marked-alt"></i> <b>Ubicación</b></span>
                <span className="loc"> {elem.location}</span>
            </p>
            <p className="uk-margin-remove-bottom">
                <span style={{color: '#333'}}><i className="fas fa-music"></i> <b className="cyan">Programa</b></span>
            </p>
            <div className="schedule">{elem.schedule && elem.schedule.length > 0 ?
                <ul className="om-text-black">
                    {elem.schedule.map((s,i) => (
                        <li key={i}> {s.name} - <span className="font-italic">{s.author}</span></li>
                    ))}
                </ul> : <p className="om-text-black uk-text-center">No se han encontrado datos.</p>
            }</div>
            {hasRole(user.permissions, ROLES.MANAGER) &&
                <FlexContainer justify="end" gap="5px" className="uk-margin-top">
                    <button className="uk-button uk-button-danger" onClick={() => setView('remove')}><i className="fa fa-trash"></i> <span className="hide-s">Eliminar</span></button>
                    <button className="uk-button uk-button-primary" onClick={() => setView('edit')}><i className="fa fa-pencil-alt"></i> <span className="hide-s">Editar</span></button>
                </FlexContainer>
            }
        </div>
    )
}

// ── EventForm (shared form for Create & Edit) ────────────────────────

const parseDate = (when) => {
    if(!when) return { date: '', time: '', plus: '' }
    const d = new Date(when)
    return {
        date: `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear()}`,
        time: `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
    }
}

const parsePlus = (when, plus) => {
    if(!when || plus === undefined) return ''
    const d = new Date(parseInt(when) + parseInt(plus))
    return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
}

const EventForm = ({ onClose, modalState, event, onSubmit = () => {}, isCreate = false, children }) => {
    const [type, setType] = useState('')
    const [date, setDate] = useState('')
    const [desc, setDesc] = useState('')
    const [loc, setLoc] = useState('')
    const [time, setTime] = useState('')
    const [plus, setPlus] = useState('')
    const [schedule, setSchedule] = useState([])
    const [notify, setNotify] = useState(false)

    const [err, setErr] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)

    useEffect(() => {
        if(modalState) {
            setErr(''); setLoading(false); setSuccess(false); setNotify(false)
            if(event) {
                setType(event.type || '')
                setDesc(event.description || event.desc || '')
                setLoc(event.location || event.loc || '')
                const parsed = parseDate(event.when)
                setDate(parsed.date)
                setTime(parsed.time)
                setPlus(parsePlus(event.when, event.plus))
                setSchedule(event.schedule ? event.schedule.map(s => ({...s})) : [])
            } else {
                setType(''); setDate(''); setDesc(''); setLoc(''); setTime(''); setPlus(''); setSchedule([])
            }
        }
    }, [event, modalState])

    const addScheduleItem = () => setSchedule([...schedule, { name: '', author: '' }])
    const removeScheduleItem = (index) => setSchedule(schedule.filter((_, i) => i !== index))
    const updateScheduleItem = (index, field, value) => {
        const updated = [...schedule]
        updated[index] = { ...updated[index], [field]: value }
        setSchedule(updated)
    }

    const formValidation = (e) => {
        e.preventDefault()
        if(!type.trim()) { setErr('Introduzca el tipo de evento'); return }
        if(!date.trim()) { setErr('Introduzca la fecha del evento'); return }
        if(!desc.trim()) { setErr('Introduzca la descripción del evento'); return }
        if(!loc.trim()) { setErr('Introduzca la ubicación'); return }
        if(!time.trim()) { setErr('Introduzca la hora de citación'); return }
        if(!plus.trim()) { setErr('Introduzca la hora del evento'); return }

        const dateMatch = date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
        if(!dateMatch) { setErr('Formato de fecha: DD/MM/YYYY'); return }
        const timeMatch = time.match(/^(\d{2}):(\d{2})$/)
        if(!timeMatch || parseInt(timeMatch[1]) >= 24) { setErr('Formato de hora citación: HH:mm'); return }
        const plusMatch = plus.match(/^(\d{2}):(\d{2})$/)
        if(!plusMatch || parseInt(plusMatch[1]) >= 24) { setErr('Formato de hora evento: HH:mm'); return }

        const day = parseInt(dateMatch[1]), month = parseInt(dateMatch[2]) - 1, year = parseInt(dateMatch[3])
        const whenTimestamp = new Date(year, month, day, parseInt(timeMatch[1]), parseInt(timeMatch[2])).getTime()
        const plusTimestamp = new Date(year, month, day, parseInt(plusMatch[1]), parseInt(plusMatch[2])).getTime()
        const plusOffset = plusTimestamp - whenTimestamp

        if(plusOffset < 0) { setErr('La hora del evento debe ser posterior a la hora de citación'); return }

        const validSchedule = schedule.filter(s => s.name.trim() && s.author.trim())

        const data = {
            type: type.trim(),
            desc: desc.trim(),
            when: whenTimestamp,
            plus: plusOffset,
            loc: loc.trim(),
            schedule: validSchedule
        }

        handleSubmitPromise(onSubmit(data, notify), setErr, setSuccess, setLoading)
    }

    return(
        <SmartForm onSubmit={formValidation} states={[loading, err, success]}>
            <div className="uk-grid-small uk-grid uk-margin-medium" uk-grid="">
                <div className="uk-width-2-3">
                    <Input className="uk-input uk-margin-small" value={type} onChange={e => setType(e.target.value)} type="text" placeholder="Tipo de evento" autoFocus/>
                </div>
                <div className="uk-width-1-3">
                    <MaskedInput className="uk-input uk-margin-small" mask="DD/MM/YYYY" value={date} onChange={setDate} type="text" placeholder="DD/MM/YYYY"/>
                </div>
                <div className="uk-width-2-3">
                    <Input className="uk-input" value={desc} onChange={e => setDesc(e.target.value)} type="text" placeholder="Descripción del Evento"/>
                </div>
                <div className="uk-width-1-3">
                    <Input className="uk-input" value={loc} onChange={e => setLoc(e.target.value)} type="text" placeholder="Ubicación aproximada"/>
                </div>
                <div className="uk-width-1-3">
                    <MaskedInput className="uk-input" mask="HH:mm" value={time} onChange={setTime} type="text" placeholder="HH:mm"/>
                </div>
                <div className="uk-width-1-3">
                    <MaskedInput className="uk-input" mask="HH:mm" value={plus} onChange={setPlus} type="text" placeholder="HH:mm"/>
                </div>
                <div className="uk-width-1-3">
                    <p className="uk-text-small uk-margin-remove">1º citación, 2º evento</p>
                </div>
                <div className="uk-width-1-1" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <p className="uk-margin-remove-bottom"><i className="fas fa-music"></i> <b className="cyan">Programa</b></p>
                    <button className="uk-button uk-button-default uk-button-small" type="button" onClick={addScheduleItem}><i className="fas fa-plus"></i> Añadir obra</button>
                </div>
                {schedule.map((s, i) => (
                    <Fragment key={i}>
                        <div className="uk-width-2-5">
                            <Input className="uk-input uk-margin-small" value={s.name} onChange={e => updateScheduleItem(i, 'name', e.target.value)} type="text" placeholder="Nombre de la obra"/>
                        </div>
                        <div className="uk-width-2-5">
                            <Input className="uk-input uk-margin-small" value={s.author} onChange={e => updateScheduleItem(i, 'author', e.target.value)} type="text" placeholder="Autor"/>
                        </div>
                        <div className="uk-width-1-5" style={{display: 'flex', alignItems: 'center'}}>
                            <button className="uk-button-small uk-button-danger" type="button" onClick={() => removeScheduleItem(i)}><i className="fas fa-minus"></i></button>
                        </div>
                    </Fragment>
                ))}
            </div>
            <ErrorMessage err={err}/>
            <SuccessMessage onHide={onClose} expand={false} success={success}>¡Guardado correctamente!</SuccessMessage>
            <LoadingBox expand={false}>
                <DefaultFooter onClose={onClose} notify={notify} setNotify={setNotify}/>
                {children}
            </LoadingBox>
        </SmartForm>
    )
}
